import React from 'react';
import { Hash, CheckCircle2, Blocks, Clock, Copy, Check } from 'lucide-react';
import { Button } from 'src/components/ui/button';
import { IEVMTransaction } from 'src/types';
import { differenceInMinutes, format } from 'date-fns';

interface TransactionInfoProps {
  transaction: IEVMTransaction;
  copiedField: string | null;
  onCopy: (text: string, field: string) => void;
}

export const EVMTransactionInfo: React.FC<TransactionInfoProps> = ({
  transaction,
  copiedField,
  onCopy,
}) => {
  return (
    <div className="rounded-lg bg-gray-50 p-6">
      <h2 className="mb-4 text-lg font-semibold">Transaction Information</h2>
      <div className="space-y-2">
        <div className="flex items-start gap-4">
          <div className="flex min-w-[200px] items-center gap-2">
            <Hash className="h-5 w-5 text-gray-500" />
            <h2 className="text-sm font-semibold text-gray-500">
              Transaction Hash
            </h2>
          </div>
          <div className="flex flex-1 items-center gap-2">
            <p className="text-md break-all font-mono">{transaction.txid}</p>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onCopy(transaction.txid, 'hash')}
              className="flex h-5 w-5 items-center justify-center hover:bg-transparent hover:text-[#0584C3]"
              title="Copy hash"
            >
              {copiedField === 'hash' ? (
                <Check className="h-3.5 w-3.5" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
            </Button>
          </div>
        </div>

        <div className="flex items-start gap-4">
          <div className="flex min-w-[200px] items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-gray-500" />
            <h2 className="text-sm font-semibold text-gray-500">Status</h2>
          </div>
          <div className="flex-1">
            <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
              Success
            </span>
          </div>
        </div>

        <div className="flex items-start gap-4">
          <div className="flex min-w-[200px] items-center gap-2">
            <Blocks className="h-5 w-5 text-gray-500" />
            <h2 className="text-sm font-semibold text-gray-500">Block</h2>
          </div>
          <div className="flex-1">
            <p className="text-md font-medium">{transaction.block}</p>
          </div>
        </div>

        <div className="flex items-start gap-4">
          <div className="flex min-w-[200px] items-center gap-2">
            <Clock className="h-5 w-5 text-gray-500" />
            <h2 className="text-sm font-semibold text-gray-500">Timestamp</h2>
          </div>
          <div className="flex-1">
            <p className="text-md font-medium">
              {format(
                new Date(transaction.timestamp * 1000),
                'MMMM d, yyyy, h:mm:ss a',
              )}
              <span className="ml-2 text-gray-500">
                •{' '}
                {(() => {
                  const date = new Date(transaction.timestamp * 1000);
                  const now = new Date();
                  const minutes = differenceInMinutes(now, date);
                  const hours = Math.floor(minutes / 60);
                  const days = Math.floor(hours / 24);

                  if (days > 0) {
                    return `(${days} day${days === 1 ? '' : 's'} ago)`;
                  }
                  if (hours > 0) {
                    return `(${hours} hour${hours === 1 ? '' : 's'} ago)`;
                  }
                  return `(${minutes} minute${minutes === 1 ? '' : 's'} ago)`;
                })()}
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
